'use client';

import React, { useState, useRef } from 'react';
import { Upload, User, ChevronRight, X, AlertCircle, Camera, Check } from 'lucide-react';
import type { WizardData } from '../lib/types';

interface StepAppearanceProps {
  gender: 'women' | 'men';
  initialValue?: WizardData['appearance'];
  onNext: (appearance: WizardData['appearance']) => void;
  error?: string | null;
}

type InputMode = 'details' | 'photo';

const SKIN_TONES = [
  { id: 'fair', label: 'Fair', hex: '#FDDBB4' },
  { id: 'light', label: 'Light', hex: '#F5C89A' },
  { id: 'medium-light', label: 'Medium Light', hex: '#E8A87C' },
  { id: 'medium', label: 'Medium', hex: '#D4845A' },
  { id: 'medium-dark', label: 'Medium Dark', hex: '#B5622E' },
  { id: 'dark', label: 'Dark', hex: '#8B4513' },
  { id: 'deep', label: 'Deep', hex: '#5C2D0E' },
];

const WOMEN_BODY_SHAPES = [
  { id: 'hourglass', label: 'Hourglass', icon: '⧗', desc: 'Balanced bust and hips, defined waist' },
  { id: 'pear', label: 'Pear', icon: '🍐', desc: 'Narrower shoulders, wider hips' },
  { id: 'apple', label: 'Apple', icon: '🍎', desc: 'Fuller midsection, slimmer legs' },
  { id: 'rectangle', label: 'Rectangle', icon: '▭', desc: 'Uniform proportions throughout' },
  { id: 'inverted-triangle', label: 'Inverted Triangle', icon: '▽', desc: 'Broader shoulders, narrower hips' },
  { id: 'petite', label: 'Petite', icon: '✦', desc: 'Smaller, compact frame' },
];

const MEN_BODY_SHAPES = [
  { id: 'athletic', label: 'Athletic', icon: '▲', desc: 'Broad shoulders, tapered waist' },
  { id: 'rectangle', label: 'Rectangle', icon: '▭', desc: 'Even proportions throughout' },
  { id: 'oval', label: 'Oval', icon: '⬭', desc: 'Fuller midsection' },
  { id: 'triangle', label: 'Triangle', icon: '▽', desc: 'Narrower shoulders, wider hips' },
  { id: 'slim', label: 'Slim', icon: '|', desc: 'Lean, narrow frame' },
];

const HAIR_COLOURS = [
  { id: 'black', label: 'Black', hex: '#1A1A1A' },
  { id: 'dark-brown', label: 'Dark Brown', hex: '#3B1F0A' },
  { id: 'medium-brown', label: 'Medium Brown', hex: '#6B3A1F' },
  { id: 'light-brown', label: 'Light Brown', hex: '#9B6B3A' },
  { id: 'auburn', label: 'Auburn', hex: '#8B3A1A' },
  { id: 'dark-blonde', label: 'Dark Blonde', hex: '#C8A96E' },
  { id: 'blonde', label: 'Blonde', hex: '#E8D090' },
  { id: 'platinum', label: 'Platinum', hex: '#F0EAD6' },
  { id: 'red', label: 'Red', hex: '#CC3300' },
  { id: 'grey', label: 'Grey', hex: '#9E9E9E' },
  { id: 'white', label: 'White', hex: '#F5F5F5' },
  { id: 'coloured', label: 'Coloured', hex: '#7B4FC4' },
];

const HEIGHT_RANGES_WOMEN = ['Under 5\'0"', '5\'0"–5\'3"', '5\'4"–5\'6"', '5\'7"–5\'9"', '5\'10" & over'];
const HEIGHT_RANGES_MEN = ['Under 5\'6"', '5\'6"–5\'8"', '5\'9"–5\'11"', '6\'0"–6\'2"', '6\'3" & over'];

const HEIGHT_RANGES_WOMEN_CM = ['Under 152 cm', '152–160 cm', '161–168 cm', '169–175 cm', '176 cm & over'];
const HEIGHT_RANGES_MEN_CM = ['Under 168 cm', '168–173 cm', '174–180 cm', '181–188 cm', '189 cm & over'];

// Map inches ranges to cm ranges by index for conversion
const INCHES_TO_CM_WOMEN: Record<string, string> = Object.fromEntries(
  HEIGHT_RANGES_WOMEN.map((v, i) => [v, HEIGHT_RANGES_WOMEN_CM[i]])
);
const CM_TO_INCHES_WOMEN: Record<string, string> = Object.fromEntries(
  HEIGHT_RANGES_WOMEN_CM.map((v, i) => [v, HEIGHT_RANGES_WOMEN[i]])
);
const INCHES_TO_CM_MEN: Record<string, string> = Object.fromEntries(
  HEIGHT_RANGES_MEN.map((v, i) => [v, HEIGHT_RANGES_MEN_CM[i]])
);
const CM_TO_INCHES_MEN: Record<string, string> = Object.fromEntries(
  HEIGHT_RANGES_MEN_CM.map((v, i) => [v, HEIGHT_RANGES_MEN[i]])
);

export default function StepAppearance({ gender, initialValue, onNext, error }: StepAppearanceProps) {
  const [inputMode, setInputMode] = useState<InputMode>('details');
  const [skinTone, setSkinTone] = useState(initialValue?.mode === 'details' ? initialValue.skinTone : '');
  const [bodyShape, setBodyShape] = useState(initialValue?.mode === 'details' ? initialValue.bodyShape : '');
  const [height, setHeight] = useState(initialValue?.mode === 'details' ? initialValue.height : '');
  const [hairColour, setHairColour] = useState(initialValue?.mode === 'details' ? initialValue.hairColour : '');
  const [heightUnit, setHeightUnit] = useState<'in' | 'cm'>('in');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bodyShapes = gender === 'women' ? WOMEN_BODY_SHAPES : MEN_BODY_SHAPES;
  const heightRangesIn = gender === 'women' ? HEIGHT_RANGES_WOMEN : HEIGHT_RANGES_MEN;
  const heightRangesCm = gender === 'women' ? HEIGHT_RANGES_WOMEN_CM : HEIGHT_RANGES_MEN_CM;
  const heightRanges = heightUnit === 'in' ? heightRangesIn : heightRangesCm;

  const handleHeightUnitToggle = (unit: 'in' | 'cm') => {
    if (unit === heightUnit) return;
    // Convert currently selected height to the new unit
    if (height) {
      if (unit === 'cm') {
        const map = gender === 'women' ? INCHES_TO_CM_WOMEN : INCHES_TO_CM_MEN;
        setHeight(map[height] ?? '');
      } else {
        const map = gender === 'women' ? CM_TO_INCHES_WOMEN : CM_TO_INCHES_MEN;
        setHeight(map[height] ?? '');
      }
    }
    setHeightUnit(unit);
  };

  const detailsComplete = skinTone && bodyShape && height && hairColour;
  const photoComplete = !!photoFile;
  const canProceed = inputMode === 'details' ? detailsComplete : photoComplete;

  const handleFileChange = (file: File) => {
    if (!file.type.startsWith('image/')) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange(file);
  };

  const handleNext = () => {
    if (!canProceed) return;
    if (inputMode === 'details') {
      onNext({ mode: 'details', skinTone, bodyShape, height, hairColour });
    } else {
      // Backend integration point: send photo to Gemini Vision API for appearance extraction
      onNext({ mode: 'photo', photoDescription: `Photo uploaded: ${photoFile?.name}` });
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 2xl:px-16 py-6 sm:py-10 lg:py-16">
      <div className="mb-6 sm:mb-8 animate-fade-in-up">
        <p className="text-xs font-semibold uppercase tracking-widest mb-2 sm:mb-3" style={{ color: '#C9A96E', letterSpacing: '0.12em' }}>
          Step 4 of 4
        </p>
        <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-2 sm:mb-3" style={{ color: '#1A1A1A' }}>
          Tell us about your look
        </h1>
        <p className="text-sm sm:text-base lg:text-lg" style={{ color: 'hsl(30 8% 52%)' }}>
          We use this to recommend the most flattering cuts, colours, and proportions.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-1 p-1 rounded-xl mb-6 sm:mb-8 max-w-xs" style={{ backgroundColor: 'hsl(35 15% 92%)' }}>
        <button
          onClick={() => setInputMode('details')}
          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150`}
          style={{
            backgroundColor: inputMode === 'details' ? 'white' : 'transparent',
            color: inputMode === 'details' ? '#1A1A1A' : 'hsl(30 8% 52%)',
            boxShadow: inputMode === 'details' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
          }}
        >
          <User size={14} />
          Fill in details
        </button>
        <button
          onClick={() => setInputMode('photo')}
          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150`}
          style={{
            backgroundColor: inputMode === 'photo' ? 'white' : 'transparent',
            color: inputMode === 'photo' ? '#1A1A1A' : 'hsl(30 8% 52%)',
            boxShadow: inputMode === 'photo' ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
          }}
        >
          <Camera size={14} />
          Upload photo
        </button>
      </div>

      {inputMode === 'details' && (
        <div className="space-y-7 sm:space-y-10 max-w-3xl animate-fade-in">
          {/* Skin tone */}
          <div>
            <label className="block text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>
              Skin tone
            </label>
            <p className="text-xs mb-3 sm:mb-4" style={{ color: 'hsl(30 8% 52%)' }}>
              Helps us suggest colours that complement your complexion
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {SKIN_TONES.map(tone => (
                <button
                  key={`tone-${tone.id}`}
                  onClick={() => setSkinTone(tone.id)}
                  className="flex flex-col items-center gap-1 sm:gap-1.5 group"
                  title={tone.label}
                  aria-pressed={skinTone === tone.id}
                >
                  <div
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 transition-all duration-150 group-hover:scale-110"
                    style={{
                      backgroundColor: tone.hex,
                      borderColor: skinTone === tone.id ? '#1A1A1A' : 'hsl(35 20% 88%)',
                      boxShadow: skinTone === tone.id ? `0 0 0 3px rgba(201,169,110,0.4)` : 'none',
                    }}
                  />
                  <span className="text-xs" style={{ color: skinTone === tone.id ? '#1A1A1A' : 'hsl(30 8% 52%)' }}>
                    {tone.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Body shape */}
          <div>
            <label className="block text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>
              Body shape
            </label>
            <p className="text-xs mb-3 sm:mb-4" style={{ color: 'hsl(30 8% 52%)' }}>
              Guides us toward silhouettes that balance and flatter your proportions
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
              {bodyShapes.map(shape => (
                <button
                  key={`shape-${shape.id}`}
                  onClick={() => setBodyShape(shape.id)}
                  className={`p-2.5 sm:p-3 rounded-xl border-2 text-left transition-all duration-150`}
                  style={{
                    borderColor: bodyShape === shape.id ? '#C9A96E' : 'hsl(35 20% 88%)',
                    backgroundColor: bodyShape === shape.id ? 'rgba(201,169,110,0.08)' : 'white',
                  }}
                  aria-pressed={bodyShape === shape.id}
                >
                  <span className="text-xl sm:text-2xl block mb-1">{shape.icon}</span>
                  <span className="block text-xs font-semibold mb-0.5 sm:mb-1" style={{ color: '#1A1A1A' }}>{shape.label}</span>
                  <span className="block text-xs leading-snug" style={{ color: 'hsl(30 8% 52%)' }}>{shape.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Height */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-semibold" style={{ color: '#1A1A1A' }}>
                Height range
              </label>
              {/* Unit toggle */}
              <div className="flex gap-0.5 p-0.5 rounded-lg" style={{ backgroundColor: 'hsl(35 15% 92%)' }}>
                <button
                  onClick={() => handleHeightUnitToggle('in')}
                  className="px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-150"
                  style={{
                    backgroundColor: heightUnit === 'in' ? 'white' : 'transparent',
                    color: heightUnit === 'in' ? '#1A1A1A' : 'hsl(30 8% 52%)',
                    boxShadow: heightUnit === 'in' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  in
                </button>
                <button
                  onClick={() => handleHeightUnitToggle('cm')}
                  className="px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-150"
                  style={{
                    backgroundColor: heightUnit === 'cm' ? 'white' : 'transparent',
                    color: heightUnit === 'cm' ? '#1A1A1A' : 'hsl(30 8% 52%)',
                    boxShadow: heightUnit === 'cm' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  }}
                >
                  cm
                </button>
              </div>
            </div>
            <p className="text-xs mb-3 sm:mb-4" style={{ color: 'hsl(30 8% 52%)' }}>
              Helps with hem lengths, proportions, and inseam recommendations
            </p>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {heightRanges.map(h => (
                <button
                  key={`height-${h}`}
                  onClick={() => setHeight(h)}
                  className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium border-2 transition-all duration-150`}
                  style={{
                    borderColor: height === h ? '#C9A96E' : 'hsl(35 20% 88%)',
                    backgroundColor: height === h ? 'rgba(201,169,110,0.08)' : 'white',
                    color: height === h ? '#1A1A1A' : 'hsl(30 8% 52%)',
                  }}
                  aria-pressed={height === h}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>

          {/* Hair colour */}
          <div>
            <label className="block text-sm font-semibold mb-1" style={{ color: '#1A1A1A' }}>
              Hair colour
            </label>
            <p className="text-xs mb-3 sm:mb-4" style={{ color: 'hsl(30 8% 52%)' }}>
              Used to suggest tonal and contrast styling choices
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3">
              {HAIR_COLOURS.map(hc => (
                <button
                  key={`hair-${hc.id}`}
                  onClick={() => setHairColour(hc.id)}
                  className="flex flex-col items-center gap-1 sm:gap-1.5 group"
                  title={hc.label}
                  aria-pressed={hairColour === hc.id}
                >
                  <div
                    className="w-7 h-7 sm:w-9 sm:h-9 rounded-full border-2 transition-all duration-150 group-hover:scale-110"
                    style={{
                      backgroundColor: hc.hex,
                      borderColor: hairColour === hc.id ? '#1A1A1A' : 'hsl(35 20% 88%)',
                      boxShadow: hairColour === hc.id ? `0 0 0 3px rgba(201,169,110,0.4)` : '0 1px 3px rgba(0,0,0,0.12)',
                    }}
                  />
                  <span className="text-xs" style={{ color: hairColour === hc.id ? '#1A1A1A' : 'hsl(30 8% 52%)' }}>
                    {hc.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {inputMode === 'photo' && (
        <div className="max-w-lg animate-fade-in">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className="relative rounded-3xl border-2 border-dashed cursor-pointer transition-all duration-200 overflow-hidden hover:scale-[1.01] hover:-translate-y-0.5"
            style={{
              borderColor: dragOver ? '#C9A96E' : photoPreview ? '#C9A96E' : 'hsl(35 20% 80%)',
              backgroundColor: dragOver ? 'rgba(201,169,110,0.06)' : 'white',
              minHeight: '220px',
              boxShadow: dragOver
                ? '0 8px 32px rgba(201,169,110,0.2)'
                : photoPreview
                ? '0 8px 24px rgba(201,169,110,0.15)'
                : '0 2px 8px rgba(0,0,0,0.06)',
            }}
            onMouseEnter={(e) => {
              if (!dragOver && !photoPreview) (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              if (!dragOver && !photoPreview) (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
            }}
          >
            {photoPreview ? (
              <div className="relative">
                <img
                  src={photoPreview}
                  alt="Uploaded appearance photo for outfit styling"
                  className="w-full object-cover rounded-3xl"
                  style={{ maxHeight: '360px' }}
                />
                <button
                  onClick={(e) => { e.stopPropagation(); setPhotoFile(null); setPhotoPreview(null); }}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 hover:opacity-80"
                  style={{ backgroundColor: '#1A1A1A' }}
                  aria-label="Remove photo"
                >
                  <X size={14} color="white" />
                </button>
                <div
                  className="absolute bottom-3 left-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: 'rgba(0,0,0,0.7)', color: 'white' }}
                >
                  <Check size={12} />
                  Photo ready
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 sm:py-16 px-6 sm:px-8 text-center">
                <div
                  className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mb-3 sm:mb-4"
                  style={{ backgroundColor: 'hsl(35 15% 92%)' }}
                >
                  <Upload size={20} style={{ color: '#C9A96E' }} />
                </div>
                <p className="text-sm sm:text-base font-semibold mb-1" style={{ color: '#1A1A1A' }}>
                  Drop your photo here
                </p>
                <p className="text-xs sm:text-sm mb-3 sm:mb-4" style={{ color: 'hsl(30 8% 52%)' }}>
                  or click to browse files
                </p>
                <p className="text-xs" style={{ color: 'hsl(30 8% 60%)' }}>
                  JPG, PNG or WEBP — max 10MB
                </p>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileChange(f); }}
            aria-label="Upload appearance photo"
          />
          <div
            className="flex items-start gap-2 sm:gap-2.5 mt-3 sm:mt-4 p-3 sm:p-3.5 rounded-xl"
            style={{ backgroundColor: 'rgba(201,169,110,0.08)', border: '1px solid rgba(201,169,110,0.2)' }}
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0" style={{ color: '#C9A96E' }} />
            <p className="text-xs leading-relaxed" style={{ color: 'hsl(30 8% 52%)' }}>
              Your photo is processed locally and never stored. It&apos;s used only to extract styling details for this session.
            </p>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-start gap-2.5 mt-5 sm:mt-6 p-3 sm:p-4 rounded-xl max-w-lg animate-fade-in" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}>
          <AlertCircle size={16} className="mt-0.5 shrink-0" style={{ color: '#E05252' }} />
          <p className="text-sm" style={{ color: '#E05252' }}>{error}</p>
        </div>
      )}

      {/* CTA */}
      <div className="mt-7 sm:mt-10">
        <button
          onClick={handleNext}
          disabled={!canProceed}
          className={`
            inline-flex items-center gap-2 sm:gap-2.5 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm font-semibold
            transition-all duration-150 active:scale-95
            ${canProceed ? 'hover:opacity-90 hover:shadow-lg' : 'opacity-40 cursor-not-allowed'}
          `}
          style={{ backgroundColor: canProceed ? '#1A1A1A' : '#8A8078', color: 'white' }}
        >
          Generate My Outfit
          <ChevronRight size={16} />
        </button>
        {!canProceed && inputMode === 'details' && (
          <p className="text-xs mt-2 sm:mt-3" style={{ color: 'hsl(30 8% 52%)' }}>
            Please fill in all four appearance details above
          </p>
        )}
      </div>
    </div>
  );
}